import React, { FC, useCallback, useEffect, useReducer, useRef } from 'react';

import {
  NativeResources,
  VulnerabilitiesApiGetVulnerabilitiesRequest,
} from '@ariksa/inventory-core/api';
import { Stack, HStack, Box, useDisclosure } from '@chakra-ui/react';
import { forEach, isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { QueryStatus } from 'services/types';
import { customTheme } from 'theme';

import { InventoryService } from 'api/services';
import { getIconColor, StackedHeader } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import {
  getPublishedTime,
  publishedAtOptions,
  Select,
  SelectHistoricalOptions,
} from 'components/DataEntry';
import { SearchBox } from 'components/DataEntry/Input/SearchBox';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { CVEAdditionalDetailsModal } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEAdditionalDetailsModal';
import { CVEReferencesModal } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEReferencesModal';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { selectActiveEnvironment } from 'containers/App/selectors';
import { useVulnerabilityTableColumns } from 'containers/Visibility/Vulnerabilities/Components/hooks/useVulnerabilityTableColumns';
import {
  hasHighPrivilegesOptions,
  internetAccessOptions,
  packageTypeOptions,
  patchAvailableOptions,
  statusOptions,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { CVEHostsDrawerDetails } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/CVE/CVEHostsDrawerDetails';
import { CVEHostsTable } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/CVEHostsTable';
import { PackagesHostsDrawerDetails } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/Package/PackagesHostsDrawerDetails';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { actions } from 'containers/Visibility/Vulnerabilities/slice';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';
import { toTitleCase } from 'utils/string';

interface Props {
  row: Record<string, any>;
  isOpen: boolean;
  onClose: () => void;
  selectedTab: string;
  fromTab?: string;
  isResolved?: boolean;
}

export const HostsDrawer: FC<Props> = props => {
  const { row, isOpen, onClose, selectedTab, fromTab = 'cve' } = props;
  const dispatch = useDispatch();
  const { vulnerabilityType, cveHosts, useVulnerabilityV3 } = useSelector(
    selectVulnerabilities,
  );

  const { withAccessBoundary } = useAccessBoundary();
  const { tableActionModals } = useVulnerabilityTableColumns();
  const { environmentId } = useSelector(selectActiveEnvironment);
  const cveInfoDisclosure = useDisclosure();
  const [searchTerm, setSearchTerm] = React.useState('');

  const ref = useRef(document.querySelector('.portal-container'));

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      historicalTime: publishedAtOptions[0],
      packageName: {},
      packageType: packageTypeOptions(selectedTab)[0],
      isPatchable: patchAvailableOptions[0],
      sortByField: {
        sortField: '',
        sortOrder: '',
      },
      showMoreLinks: false,
      status: statusOptions[vulnerabilityType === 'open' ? 0 : 1],
      internetAccess: internetAccessOptions[0],
      hasHighPrivileges: hasHighPrivilegesOptions[0],
    },
  );

  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  //set resource type
  useEffect(() => {
    const onSuccess = res => {
      if (isEmpty(res?.items)) return;
      const data: Record<string, any[]> = {};
      forEach(res?.items, (o: Record<string, any>) =>
        o?.native_name
          ? !!data[o?.native_name]
            ? data[o?.native_name].push(o)
            : (data[o.native_name] = [o])
          : '',
      );

      forEach(data, (items, key) => {
        const payload: VulnerabilitiesApiGetVulnerabilitiesRequest = {
          resourceVulnerabilitiesRequest: {
            resource_ids: map(items, o => o?.uuid),
            native_resource: key as NativeResources,
          },
        };
        dispatch(
          actions.getCveHostsVulnerabilities({
            q: payload,
            status: QueryStatus.pending,
          }),
        );
        if (useVulnerabilityV3) {
          // ScanVault host rows already carry their OS and counts.
          dispatch(
            actions.getCveHostsVulnerabilities({
              q: payload,
              status: QueryStatus.fulfilled,
              data: map(items, o => ({ ...o, resource_id: o?.uuid })),
            }),
          );
          return;
        }
        // A failed request still ends the Image column's loading state.
        InventoryService.Vulnerability.getVulnerabilities(payload)
          .then(res => {
            dispatch(
              actions.getCveHostsVulnerabilities({
                q: payload,
                status: QueryStatus.fulfilled,
                data: res.data,
              }),
            );
          })
          .catch(() => {
            dispatch(
              actions.getCveHostsVulnerabilities({
                q: payload,
                status: QueryStatus.failed,
              }),
            );
          });
      });

      /*get risk context*/
      dispatch(
        actions.getCveHostsRiskContext({
          q: { riskContextRequest: { uuids: map(res?.items, o => o?.uuid) } },
        }),
      );
    };

    const commonPayload = {
      resolved: state.status?.value,
      isPatchable: !!state.isPatchable?.value
        ? state.isPatchable?.value === 'yes'
        : undefined,
      packageCategory: state.packageType?.value,
      ...(state.status?.value
        ? { resolvedAt: getPublishedTime(state.historicalTime?.value) }
        : { effectiveStart: getPublishedTime(state.historicalTime?.value) }),
      page: cveHosts.page.info.page_number,
      size: cveHosts.page.info.page_size,
    };

    if (selectedTab === 'cve')
      dispatch(
        actions.getCveHosts({
          q: {
            environmentId: environmentId,
            cveId: row.vulnerability_id ?? row?.cve_id,
            ...commonPayload,
          },
          onSuccess,
        }),
      );
    else
      dispatch(
        actions.getPackageHosts({
          q: withAccessBoundary({
            name: row?.package_name,
            version: row?.installed_version,
            ...commonPayload,
          }),
          onSuccess,
        }),
      );
  }, [
    state.status,
    state.isPatchable,
    row,
    dispatch,
    withAccessBoundary,
    state.historicalTime,
    cveHosts.page.info,
    selectedTab,
    state.packageType,
    environmentId,
    useVulnerabilityV3,
  ]);

  const renderCVEHeader = () =>
    (row?.package_name && selectedTab !== 'cve'
      ? row?.vulnerability_id + ', '
      : '') +
    (row?.cvss_score || !isEmpty(row?.cvss_v3)
      ? 'CVSS V3 Score: ' +
        Number(row?.cvss_score ?? row?.cvss_v3?.score).toFixed(1) +
        ' '
      : '') +
    ', Severity: ' +
    toTitleCase(row?.severity);

  const renderPackagesHeader = () => row?.installed_version;
  const styles = {
    menu: provided => ({
      ...provided,
      width: 'max-content',
      minWidth: '100%',
      right: 0,
    }),
  };

  useEffect(() => {
    dispatch(actions.resetFilters());
  }, [dispatch]);

  const header = (
    <StackedHeader
      upper={
        selectedTab === 'cve' || fromTab === VulnerabilityViewByTabs.Package
          ? row?.vulnerability_id ?? row?.cve_id
          : row?.package_name
      }
      lower={
        selectedTab === 'cve'
          ? fromTab === VulnerabilityViewByTabs.Package
            ? row?.package_name + ' - ' + row?.installed_version
            : renderCVEHeader()
          : renderPackagesHeader()
      }
      type={
        selectedTab === 'cve'
          ? IconTypes.Vulnerabilities
          : row?.package_category_name ?? IconTypes.Packages
      }
      color={
        selectedTab === 'cve'
          ? customTheme.colors?.[row?.severity?.toLowerCase()]
          : getIconColor(row?.package_category_name)
      }
      useCustomColor
    />
  );

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={header}
        body={
          <Stack w="full" h="full" spacing={5}>
            {selectedTab === VulnerabilityViewByTabs.Package ? (
              <PackagesHostsDrawerDetails row={row} />
            ) : (
              <CVEHostsDrawerDetails row={row} selectedTab={fromTab} />
            )}
            <BorderedBox flex={1} w="full" h="full" minH="400px">
              <Stack h="full" spacing={5} w="full">
                <HStack w="full">
                  <Box w={64}>
                    <SearchBox
                      onChange={s => setSearchTerm(s)}
                      value={searchTerm}
                      placeholder="Search"
                      isDisabled
                    />
                  </Box>
                  <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                  {!useVulnerabilityV3 && (
                    <Box zIndex={900}>
                      <Select
                        options={internetAccessOptions}
                        value={state?.internetAccess}
                        onChange={s => updateValue('internetAccess', s)}
                        showIconInValueContainer
                        styles={styles}
                        menuPortalTarget={document.body}
                        leftMessage="Internet Access"
                        isAFilter
                        showUndefinedValueLabel={false}
                        isDisabled
                      />
                    </Box>
                  )}
                  <Box zIndex={900}>
                    <Select
                      options={hasHighPrivilegesOptions}
                      value={state?.hasHighPrivileges}
                      onChange={s => updateValue('hasHighPrivileges', s)}
                      showIconInValueContainer
                      styles={styles}
                      menuPortalTarget={document.body}
                      leftMessage="Has High Privileges"
                      isAFilter
                      showUndefinedValueLabel={false}
                      isDisabled
                    />
                  </Box>

                  {/*{selectedTab === 'cve' && (
                    <>
                      patch status
                      <Box zIndex={900}>
                        <Select
                          options={patchAvailableOptions}
                          value={state.isPatchable}
                          onChange={s => updateValue('isPatchable', s)}
                          leftMessage="Patchable"
                          isAFilter
                          showIconInValueContainer
                          styles={styles}
                          menuPortalTarget={document.body}
                          showUndefinedValueLabel={false}
                        />
                      </Box>

                      //all types
                      <Box zIndex={900}>
                        <Select
                          options={packageTypeOptions('cve')}
                          value={state.packageType}
                          onChange={s => updateValue('packageType', s)}
                          showIconInValueContainer
                          styles={styles}
                          leftMessage="Package type"
                          isAFilter
                          menuPortalTarget={document.body}
                          showUndefinedValueLabel={false}
                        />
                      </Box>
                    </>
                  )}*/}
                  <Box>
                    <SelectHistoricalOptions
                      historicalTime={state.historicalTime}
                      setHistoricalTime={s => updateValue('historicalTime', s)}
                      isAFilter
                      leftMessage="Timeline"
                      width="full"
                    />
                  </Box>
                </HStack>
                <ActiveResourceProvider>
                  <CVEHostsTable
                    sortByField={state.sortByField}
                    setSortByField={s => updateValue('sortByField', s)}
                    currentRecord={row}
                  />
                  {tableActionModals}
                </ActiveResourceProvider>
              </Stack>
            </BorderedBox>
          </Stack>
        }
        closeButton
        styles={{
          content: { maxW: '1550px' },
          //drawer: { portalProps: { containerRef: ref as any } },
        }}
      />
      {state.showMoreLinks && (
        <CVEReferencesModal
          isOpen={state.showMoreLinks}
          onClose={() => updateValue('showMoreLinks', false)}
          cveDetails={row}
        />
      )}
      {cveInfoDisclosure.isOpen && (
        <CVEAdditionalDetailsModal
          cveDetails={row}
          isOpen={cveInfoDisclosure.isOpen}
          onClose={cveInfoDisclosure.onClose}
        />
      )}
    </>
  );
};
